<template lang="pug">
div(v-if="show")
  .treasure-box-float(@click="handleClick" v-platform-expose="handleModuleExpose")
    //- 可开状态 AVAILABLE
    template(v-if="isAvailable")
      //- 长图（进入动画，3s 后收起）
      .treasure-box-long-wrap(
        v-if="showLongImg"
        :class="{ collapsing: longImgCollapsing }"
        :style="{ '--long-img': `url(${floatInfo.floatLongImg})` }"
      )
        img.treasure-box-long-img(:src="floatInfo.floatLongImg")
      //- 短图（摇晃）+ 文案图（层级在上）
      template(v-else)
        img.treasure-box-short-img(:src="floatInfo.floatShortImg")
        .treasure-box-short-text-wrap(
          :style="{ '--text-img': `url(${floatInfo.floatShortTextImg})` }"
        )
          img.treasure-box-short-text-img(:src="floatInfo.floatShortTextImg")
    //- 冷却状态 COOLING
    template(v-else-if="isCooling")
      img.treasure-box-short-img.no-shake(:src="floatInfo.floatShortImg")
      .treasure-box-label(v-if="countdownText")
        span.label-highlight {{ countdownText }}
        span.label-text 后领
  //- 选择奖励弹窗
  SelectRewardPopup(
    :visible="showSelectPopup"
    :normalBoxReward="floatInfo.normalBoxReward"
    :videoBoxReward="floatInfo.videoBoxReward + floatInfo.normalBoxReward"
    :strategyCode="floatInfo.strategyCode"
    @close="handleSelectPopupClose"
    @select-base="handleSelectBase"
    @select-video="handleSelectVideo"
  )

  //- 开箱结果弹窗
  RewardPopup(
    :visible="showRewardPopup"
    :treasureList="treasureList"
    :styleType="rewardStyleType"
    :buttonText="buttonText"
    :todayTotalReward="todayTotalReward"
    :parentCountdownText="countdownText"
    :canContinue="canContinue"
    :strategyCode="floatInfo.strategyCode"
    @close="handleRewardPopupClose"
    @claim-extra="handleClaimExtra"
    @countdown-end="handleCountdownEnd"
    @open-box="handleOpenBox"
  )
</template>

<script>
import eventBus from '@/const/eventBus';
import { openTreasureBox, reportVideoComplete, getTreasureBoxFloatInfo } from '@/api/fuli';
import { getTokenAndLocation } from '@/utils';
import Loading from '@/components/loading';
import SelectRewardPopup from './select-reward-popup';
import RewardPopup from './reward-popup';
import { playContinuousVideo } from '../../../common/continuous-video-engine';
import SPM from '../../../common/spm';

export default {
  name: 'TreasureBoxFloat',
  components: {
    SelectRewardPopup,
    RewardPopup,
  },
  props: {
    moduleData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      floatInfo: {},
      show: false,
      showLongImg: false,
      longImgCollapsing: false,
      countdownText: '',
      countdownTimer: null,
      longImgTimer: null,
      longImgCollapseTimer: null,
      // 选择弹窗显示
      showSelectPopup: false,
      // 宝箱弹窗显示
      showRewardPopup: false,
      // 宝箱弹窗样式控制
      rewardStyleType: '',
      // 宝箱开启时间戳
      nextAvailableTime: 0,
      // 宝箱的进度
      treasureList: [],
      // 宝箱按钮文案
      buttonText: '',
      todayTotalReward: 0,
      // 当前状态（本地维护，接口刷新后更新）
      status: '',
      token: '',
      cityCode: '',
      canContinue: true,
      currentIndex: 0,
      videoTaskId: '',
      adResourceId: '',
      // 防重复点击锁
      isProcessing: false,
      // 当前宝箱剩余观看次数
      remainingVideoCount: 0,
    };
  },
  computed: {
    isAvailable() {
      return this.status === 'AVAILABLE';
    },
    isCooling() {
      return this.status === 'COOLING';
    },
  },
  async mounted() {
    try {
      const { token, cityCode } = await getTokenAndLocation();
      this.token = token || '';
      this.cityCode = cityCode || '';
      await this.fetchFloatInfo();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('TreasureBoxFloat mount error', e);
    }
  },
  beforeDestroy() {
    this.clearLongImgTimer();
    this.clearCountdownTimer();
  },
  methods: {
    init(info) {
      this.syncFloatInfo(info);
      this.applyStatus(
        info.status || this.status,
        info.nextAvailableTime || this.nextAvailableTime,
      );
    },
    syncFloatInfo(info = {}) {
      Object.keys(info).forEach((key) => {
        if (info[key] !== undefined && key in this) {
          this[key] = info[key];
        }
      });
    },
    applyStatus(status, nextAvailableTime) {
      this.clearLongImgTimer();
      this.clearCountdownTimer();

      if (status) this.status = status;

      if (status === 'AVAILABLE') {
        this.countdownText = '';
        this.showLongImg = true;
        this.longImgCollapsing = false;
        this.longImgTimer = setTimeout(() => {
          this.longImgCollapsing = true;
          this.longImgCollapseTimer = setTimeout(() => {
            this.showLongImg = false;
            this.longImgCollapsing = false;
          }, 400);
        }, 3000);
      } else if (status === 'COOLING') {
        this.showLongImg = false;
        this.startFloatCountdown(nextAvailableTime);
      } else {
        this.showLongImg = false;
        this.countdownText = '';
      }
    },
    clearLongImgTimer() {
      if (this.longImgTimer) {
        clearTimeout(this.longImgTimer);
        this.longImgTimer = null;
      }
      if (this.longImgCollapseTimer) {
        clearTimeout(this.longImgCollapseTimer);
        this.longImgCollapseTimer = null;
      }
    },
    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    startFloatCountdown(timestamp) {
      this.clearCountdownTimer();
      if (!timestamp) {
        this.countdownText = '';
        return;
      }
      const targetMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;
      const update = () => {
        const diff = targetMs - Date.now();
        if (diff <= 0) {
          this.clearCountdownTimer();
          this.countdownText = '';
          if (this.showRewardPopup) {
            this.handleCountdownEnd();
            return;
          }
          this.refreshFloatInfo();
          return;
        }
        const totalSeconds = Math.floor(diff / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => String(n).padStart(2, '0');
        this.countdownText = `${pad(minutes)}:${pad(seconds)}`;
      };
      update();
      this.countdownTimer = setInterval(update, 1000);
    },
    // 模块曝光埋点
    handleModuleExpose() {
      SPM.moduleExpose({
        moduleId: 'platform_floating_widget',
        contentId: 'platform_floating_widget',
        businessInfo: {
          boxgamestatus: this.status === 'AVAILABLE' ? '1' : '2',
          boxgamecode: this.floatInfo.strategyCode || '',
        },
      });
    },
    handleClick() {
      SPM.clickButton({
        buttonName: 'platform_floating_widget',
        businessInfo: {
          boxgamestatus: this.status === 'AVAILABLE' ? '1' : '2',
          boxgamecode: this.floatInfo.strategyCode || '',
        },
      });
      if (!this.status) return;
      if (this.status === 'AVAILABLE') {
        if (this.canContinue) {
          this.showSelectPopup = true;
        } else {
          this.handleSelectBase();
        }
      } else {
        this.rewardStyleType = 'close';
        this.showRewardPopup = true;
      }
    },

    // 获取/刷新宝箱浮标数据
    async fetchFloatInfo() {
      try {
        const { data } = (await getTreasureBoxFloatInfo({
          token: this.token,
          cityCode: this.cityCode,
        })) || {};
        if (data) {
          this.floatInfo = data;
          this.show = !!data.show;
          this.init(data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchFloatInfo error', e);
        this.applyStatus(this.status, this.nextAvailableTime);
      }
    },

    // 刷新宝箱浮标数据（fetchFloatInfo 的别名，语义更清晰）
    refreshFloatInfo() {
      return this.fetchFloatInfo();
    },

    // 关闭选择弹窗
    handleSelectPopupClose() {
      this.showSelectPopup = false;
    },

    // 关闭奖励弹窗
    handleRewardPopupClose() {
      this.showRewardPopup = false;
    },

    // 倒计时结束后以接口最新状态为准
    handleCountdownEnd() {
      if (!this.showRewardPopup) return;
      // 弹窗保持打开，浮标正常刷新播动画
      this.refreshFloatInfo();
    },

    // 倒计时结束后用户点击「开宝箱」
    handleOpenBox() {
      this.showRewardPopup = false;
      if (this.canContinue) {
        this.showSelectPopup = true;
      } else {
        this.handleSelectBase();
      }
    },

    // 开箱成功后的通用处理
    handleBoxOpened(data) {
      this.rewardStyleType = 'open';
      this.syncFloatInfo(data);
      this.showSelectPopup = false;
      this.showRewardPopup = true;
      eventBus.$emit('playCollectAnimate');
      this.$emit('bounty');
      this.refreshFloatInfo();
    },

    // 选择基础奖励
    async handleSelectBase() {
      if (this.isProcessing) return;
      this.isProcessing = true;
      Loading.show();
      try {
        const { data } = (await openTreasureBox({
          token: this.token,
          cityCode: this.cityCode,
          boxType: 1,
        })) || {};
        if (data) {
          this.handleBoxOpened(data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('openTreasureBox error', e);
      }
      Loading.close();
      this.isProcessing = false;
    },

    // 构建视频播放配置
    buildVideoConfig(toastOverrides = {}, options = {}) {
      const { includeNormalReward = false } = options;
      const rewardCount = includeNormalReward
        ? this.floatInfo.videoBoxReward + this.floatInfo.normalBoxReward
        : this.floatInfo.videoBoxReward;
      return {
        firstVideoParams: {
          adUnitId: this.adResourceId,
          taskCode: this.videoTaskId,
          rewardCount,
        },
        getNextVideoParams: () => {
          if (this.remainingVideoCount < 1) return null;
          return {
            adUnitId: this.adResourceId,
            taskCode: this.videoTaskId,
            rewardCount: this.floatInfo.videoBoxReward,
          };
        },
        toastConfig: toastOverrides,
        maxCount: this.remainingVideoCount,
        finishTaskInfo: this.floatInfo.bannerTaskRelationCouponInfo,
      };
    },

    // 选择视频奖励
    async handleSelectVideo() {
      if (this.isProcessing) return;
      this.isProcessing = true;
      let boxOpened = false;
      const { success, completedCount } = await playContinuousVideo({
        ...this.buildVideoConfig({
          none_completed_failed: '视频加载失败，请稍后重试',
          none_completed_closed: '观看中断未减时间，请重新点击观看',
          mid_video_failed: '视频加载失败，请稍后重试',
          mid_video_closed: '观看中断未减时间，请重新点击观看',
        }, { includeNormalReward: true }),
        callbacks: {
          onVideoComplete: async ({ currentIndex }) => {
            try {
              if (currentIndex === 0) {
                // 第一个视频完成：调开宝箱接口
                const { data } = (await openTreasureBox({
                  token: this.token,
                  cityCode: this.cityCode,
                  boxType: 2,
                  videoCount: 1,
                })) || {};
                if (data) {
                  boxOpened = true;
                  this.syncFloatInfo(data);
                }
              } else {
                // 后续视频完成：调上报视频接口
                const { data } = (await reportVideoComplete({
                  token: this.token,
                  cityCode: this.cityCode,
                  videoCount: 1,
                  currentBoxIndex: this.currentIndex,
                })) || {};
                if (data) {
                  this.syncFloatInfo(data);
                }
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('handleSelectVideo onVideoComplete error', e);
            }
          },
        },
      });
      if (boxOpened || success || completedCount > 0) {
        this.rewardStyleType = 'open';
        this.showSelectPopup = false;
        this.showRewardPopup = true;
        eventBus.$emit('playCollectAnimate');
        this.$emit('bounty');
        this.refreshFloatInfo();
      }
      this.isProcessing = false;
    },

    // 领取额外奖励
    async handleClaimExtra() {
      if (this.isProcessing || !this.canContinue) return;
      this.isProcessing = true;
      let reported = false;
      const { success, completedCount } = await playContinuousVideo({
        ...this.buildVideoConfig({
          none_completed_failed: '视频加载失败，请稍后重试',
          none_completed_closed: '任务未完成',
          mid_video_failed: '视频加载失败，请稍后重试',
          mid_video_closed: '任务未完成',
        }),
        callbacks: {
          onVideoComplete: async () => {
            try {
              const { data } = (await reportVideoComplete({
                token: this.token,
                cityCode: this.cityCode,
                videoCount: 1,
                currentBoxIndex: this.currentIndex,
              })) || {};
              if (data) {
                reported = true;
                this.syncFloatInfo(data);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('handleClaimExtra onVideoComplete error', e);
            }
          },
        },
      });
      if (reported || success || completedCount > 0) {
        this.$emit('bounty');
      }
      this.isProcessing = false;
    },
  },
};
</script>

<style lang="less" scoped>
.treasure-box-float {
  position: fixed;
  right: 0;
  bottom: 1rem;
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;

  @keyframes shake {
    0%,
    5% { transform: rotate(0deg); }
    8% { transform: rotate(-10deg); }
    12% { transform: rotate(10deg); }
    16% { transform: rotate(-8deg); }
    20% { transform: rotate(8deg); }
    24% { transform: rotate(-5deg); }

    27%,
    100% { transform: rotate(0deg); }
  }

  @keyframes sweep-light {
    0% { left: -60%; }
    100% { left: 130%; }
  }

  .treasure-box-long-wrap {
    position: relative;
    top: 0.3rem;
    overflow: hidden;
    transform-origin: right top;
    transition: transform 0.4s ease-in;

    // 用图片自身作为 mask，扫光只在图片非透明区域可见
    -webkit-mask-image: var(--long-img);
    mask-image: var(--long-img);
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;

    &.collapsing {
      transform: translateX(100%);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -60%;
      width: 40%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      transform: skewX(-20deg);
      animation: sweep-light 1.2s ease-in-out infinite;
      pointer-events: none;
      z-index: 2;
    }
  }

  .treasure-box-long-img {
    width: auto;
    height: 74px;
  }

  .treasure-box-short-img {
    width: 0.6rem;
    height: auto;
    animation: shake 4s ease-in-out infinite;
    position: relative;
    margin-right: 0.12rem;

    &.no-shake {
      animation: none;
    }
  }

  .treasure-box-short-text-wrap {
    position: absolute;
    bottom: -0.21rem;
    margin-right: 0.12rem;
    width: 0.82rem;
    z-index: 1;
    animation: breathe 4s ease-in-out infinite;
    overflow: hidden;

    // 用图片自身作为 mask，扫光只在非透明区域可见
    -webkit-mask-image: var(--text-img);
    mask-image: var(--text-img);
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 40%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      transform: skewX(-20deg);
      animation: text-sweep 4s ease-in-out infinite;
      pointer-events: none;
    }

    .treasure-box-short-text-img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  @keyframes breathe {
    0%,
    27% { transform: scale(1); }
    50% { transform: scale(1.08); }

    73%,
    100% { transform: scale(1); }
  }

  @keyframes text-sweep {
    0%,
    73% { left: -100%; }
    85% { left: 130%; }

    86%,
    100% { left: 130%; }
  }

  // 倒计时文案条（平行四边形）
  .treasure-box-label {
    position: absolute;
    bottom: -0.15rem;
    width: 0.72rem;
    height: 0.22rem;
    margin-right: 0.12rem;
    padding: 4.5px 7.5px;
    background: linear-gradient(90deg, #ff5523 0%, #ff3e36 100%);
    border-radius: 4px;
    transform: skewX(-5deg);
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.07);
    border: 0.005rem solid #fff;

    .label-text {
      margin-left: 0.02rem;
      font-size: 0.14rem;
      color: #fff;
      display: inline-block;
      transform: skewX(5deg);
      font-weight: 600;
    }

    .label-highlight {
      width: 0.33rem;
      font-family: 'DIN Alternate Bold', sans-serif;
      color: #fff;
      display: inline-block;
      transform: skewX(5deg);
      font-weight: 700;
      font-style: bold;
      font-size: 0.15rem;
    }
  }
}
</style>
